/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Badge,
  Button,
  CheckIcon,
  ErrorMessage,
  Image,
  Link,
  Modal,
  ModalOpenBtn,
  useEffect,
  useState,
} from "@";
import {
  EyeIcon,
  PaintBrushIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { useInfiniteThemesQuery } from "../api/queries";
import { getThemeContentByUrl } from "../api/api";
import { renderProjectTemplate } from "../lib/render-template";
import type { SearchValues, Theme } from "../types";
import { injectMockProjectDetails, mockProject } from "../lib/preview-mock";
import {
  cardEmptySurface,
  cardFooter,
  cardGridGap,
  cardMedia,
  cardMediaImg,
  cardOverlayTitle,
  cardScrim,
  cardSkeletonBlock,
  cardSkeletonSurface,
  cardSurface,
  cardSurfaceSelected,
} from "../../shared/cards";

interface Props {
  /** Picker mode — currently selected theme id. Omit for management/showcase mode. */
  value?: number | string | null;
  /** Picker mode — fires when a card is chosen. When omitted, cards are not selectable. */
  onChange?: (id?: number) => void;
  disabled?: boolean;
  error?: string;
  /**
   * Data exposed as variables to the Liquid template when rendering the
   * preview. Spread directly onto the engine context, so e.g.
   * `elementData={{ project }}` makes `{{ project.title }}` available.
   */
  elementData?: any;
  /**
   * Filter passed to the (infinite) themes query. Defaults to published-only,
   * which is what the picker contexts want; the management list page passes
   * the user's applied search filters here.
   */
  search?: SearchValues;
  /** Per-card actions rendered in the card footer (edit / delete / toggle, …). */
  renderActions?: (theme: Theme) => React.ReactNode;
}

const PREVIEW_MODAL_ID = "theme-gallery-preview-modal";

const DEFAULT_SEARCH: SearchValues = { title: "", isPublished: "true" };

const GRID_CLASS = `grid ${cardGridGap} sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`;

const SkeletonCard = () => (
  <div className={cardSkeletonSurface}>
    <div className="aspect-[5/3] w-full animate-pulse bg-gray-200 dark:bg-gray-700" />
    <div className="space-y-2 border-t border-gray-100 p-4 dark:border-gray-700/60">
      <div className={`h-3.5 w-3/4 ${cardSkeletonBlock}`} />
      <div className={`h-3 w-1/2 ${cardSkeletonBlock}`} />
    </div>
  </div>
);

export const ThemeGallery = ({
  value,
  onChange,
  disabled,
  error,
  elementData = mockProject,
  search,
  renderActions,
}: Props) => {
  const [previewTheme, setPreviewTheme] = useState<Theme | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [previewError, setPreviewError] = useState<string | null>(null);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteThemesQuery(search ?? DEFAULT_SEARCH);

  const themes: Theme[] = data?.pages.flatMap((page) => page.items ?? []) ?? [];
  const isPicker = typeof onChange === "function";

  useEffect(() => {
    if (!previewTheme?.themeUrl) {
      setPreviewHtml("");
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: template } = await getThemeContentByUrl(
          previewTheme.themeUrl,
        );
        const rendered = await renderProjectTemplate(
          template,
          elementData ?? {},
        );
        const html = injectMockProjectDetails(rendered, {
          projectDetails:
            elementData?.projectDetails || mockProject.projectDetails,
          imageUrl:
            elementData?.projectDescriptionImagesUrls?.[0] ??
            mockProject.projectDescriptionImagesUrls[0] ??
            null,
        });
        if (!cancelled) {
          setPreviewHtml(html);
          setPreviewError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setPreviewHtml("");
          setPreviewError(
            err instanceof Error ? err.message : "تعذّر تحميل الثيم",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [previewTheme, elementData]);

  const selectedId = value != null ? Number(value) : null;

  const handleCardSelect = (id: number) => {
    if (disabled || !isPicker) return;

    if (id === selectedId){
      onChange!(undefined)
      return
    }

    console.log(`Selecting theme with ID: ${id}`, selectedId);
    onChange!(id);
  };

  const handleCardKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    id: number,
  ) => {
    if (disabled || !isPicker) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onChange!(id);
    }
  };

  if (isLoading && themes.length === 0) {
    return (
      <div className={GRID_CLASS}>
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (!isLoading && themes.length === 0) {
    return (
      <div
        className={`flex flex-col items-center justify-center px-6 py-12 text-center ${cardEmptySurface}`}
      >
        <span className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-white text-purple-700 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-purple-300 dark:ring-gray-700">
          <PaintBrushIcon className="size-7" />
        </span>
        <p className="text-base font-semibold text-gray-800 dark:text-gray-100">
          لا توجد ثيمات بعد
        </p>
        <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
          أنشئ ثيماً واحداً على الأقل ليظهر هنا.
        </p>
        <Link
          className="mt-4 inline-flex items-center gap-1.5 rounded-2xl bg-purple-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-purple-950 dark:bg-purple-700 dark:hover:bg-purple-600"
          to="/website/themes/create"
        >
          <SparklesIcon className="size-4" />
          إضافة ثيم جديد
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className={GRID_CLASS}>
        {themes.map((theme) => {
          const isSelected = isPicker && selectedId === theme.id;
          return (
            <div
              key={theme.id}
              role={isPicker ? "button" : undefined}
              tabIndex={isPicker ? (disabled ? -1 : 0) : undefined}
              aria-pressed={isPicker ? isSelected : undefined}
              aria-disabled={disabled}
              onClick={isPicker ? () => handleCardSelect(theme.id) : undefined}
              onKeyDown={
                isPicker ? (e) => handleCardKeyDown(e, theme.id) : undefined
              }
              className={`${cardSurface} text-start outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                isPicker
                  ? disabled
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer"
                  : ""
              } ${isSelected ? cardSurfaceSelected : ""}`}
            >
              {/* Media frame */}
              <div className={`${cardMedia} aspect-[5/3]`}>
                <Image
                  click={() => {}}
                  className={cardMediaImg}
                  src={theme.thumbnailUrl}
                  alt={theme.title}
                  size="500"
                />

                {/* Bottom scrim so the overlaid title stays legible */}
                <span aria-hidden className={cardScrim} />

                {/* Preview button — top-end */}
                <div
                  className="absolute end-3 top-3"
                >
                  <ModalOpenBtn
                    title="معاينة"
                    variant="btn-white px-3 py-2"
                    target={PREVIEW_MODAL_ID}
                    handleClick={() => setPreviewTheme(theme)}
                  >
                    <EyeIcon className="h-5 w-5" />
                  </ModalOpenBtn>
                </div>

                {/* Top-start: selected badge (picker) or status pill (management) */}
                {isSelected ? (
                  <span className="absolute start-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-purple-700 px-2.5 py-1 text-[11px] font-bold text-white shadow-sm ring-1 ring-white/20 dark:bg-purple-500">
                    <CheckIcon className="size-3.5" />
                    محدد
                  </span>
                ) : (
                  !isPicker && (
                    <span className="absolute start-3 top-3 inline-flex items-center">
                      <Badge
                        color={theme.isPublished ? "green" : "red"}
                        title={theme.isPublished ? "منشورة" : "مسودة"}
                      />
                    </span>
                  )
                )}

                {/* Title overlaid on the scrim */}
                <h3 className={cardOverlayTitle} title={theme.title}>
                  {theme.title}
                </h3>
              </div>

              {/* Footer — pick affordance (picker) or row actions (management) */}
              {isPicker ? (
                <div className={`${cardFooter} justify-center`}>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold transition ${
                      isSelected
                        ? "bg-purple-700 text-white dark:bg-purple-500"
                        : "bg-purple-50 text-purple-800 group-hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-200"
                    }`}
                    aria-hidden
                  >
                    <CheckIcon className="size-3.5" />
                    {isSelected ? "محدد" : "اختيار هذا الثيم"}
                  </span>
                </div>
              ) : renderActions ? (
                <div
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  className={`${cardFooter} justify-center`}
                >
                  {renderActions(theme)}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-center py-8">
        {hasNextPage ? (
          <Button
            type="button"
            title="تحميل المزيد"
            isLoading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
            className="!w-fit px-6"
          />
        ) : (
          themes.length > 0 && (
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-gray-300 dark:text-gray-600">
              نهاية القائمة
            </span>
          )
        )}
      </div>

      {error && <ErrorMessage value={error} />}

      <Modal
        id={PREVIEW_MODAL_ID}
        size="max-w-8xl"
        title={previewTheme ? `معاينة: ${previewTheme.title}` : "معاينة الثيم"}
        close={() => setPreviewTheme(null)}
      >
        {previewError ? (
          <pre
            dir="ltr"
            className="m-4 max-h-[80vh] overflow-auto rounded-md bg-red-50 p-4 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            {previewError}
          </pre>
        ) : previewHtml ? (
          <iframe
            srcDoc={previewHtml}
            title={
              previewTheme
                ? `Theme preview: ${previewTheme.title}`
                : "Theme preview"
            }
            className="h-[80vh] w-full border-0 bg-gray-100"
            sandbox="allow-same-origin"
          />
        ) : previewTheme ? (
          <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
            <span className="size-10 animate-spin rounded-full border-2 border-purple-300 border-t-purple-700" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              جاري تحميل الثيم...
            </p>
          </div>
        ) : (
          <p className="p-6 text-center text-sm text-gray-500">
            لا يوجد ثيم محدّد للمعاينة.
          </p>
        )}
      </Modal>
    </>
  );
};
